import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { AxiosInstance, InternalAxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';

interface RequestLog {
  id: number;
  method: string;
  url: string;
  status?: number;
  timestamp: string;
  requestBody?: any;
  responseBody?: any;
  error?: string;
}

interface RequestLoggerContextType {
  logs: RequestLog[];
  clearLogs: () => void; // Додаємо функцію очищення
}

const RequestLoggerContext = createContext<RequestLoggerContextType | undefined>(undefined);

interface RequestLoggerProviderProps {
  children: ReactNode;
  axiosInstance: AxiosInstance;
}

export const RequestLoggerProvider: React.FC<RequestLoggerProviderProps> = ({ children, axiosInstance }) => {
  const [logs, setLogs] = useState<RequestLog[]>([]);
  const nextIdRef = useRef(0);

  const clearLogs = () => setLogs([]); // Функція для очищення логів

  useEffect(() => {
    const requestInterceptor = axiosInstance.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        const id = nextIdRef.current++;
        const newLog: RequestLog = {
          id,
          method: (config.method || 'GET').toUpperCase(),
          url: config.url || '',
          timestamp: new Date().toISOString(),
          requestBody: config.data,
        };
        setLogs((prevLogs) => [newLog, ...prevLogs]); // Додаємо новий лог на початок
        return config;
      },
      (error: AxiosError) => Promise.reject(error)
    );

    const responseInterceptor = axiosInstance.interceptors.response.use(
      (response: AxiosResponse) => {
        setLogs((prevLogs) =>
          prevLogs.map((log) =>
            log.url === response.config.url && log.method === (response.config.method || 'GET').toUpperCase() && !log.status
              ? { ...log, status: response.status, responseBody: response.data }
              : log
          )
        );
        return response;
      },
      (error: AxiosError) => {
        setLogs((prevLogs) =>
          prevLogs.map((log) =>
            log.url === error.config?.url && log.method === (error.config?.method || 'GET').toUpperCase() && !log.status
              ? { ...log, status: error.response?.status, error: error.message || 'Network error' }
              : log
          )
        );
        return Promise.reject(error);
      }
    );

    return () => {
      axiosInstance.interceptors.request.eject(requestInterceptor);
      axiosInstance.interceptors.response.eject(responseInterceptor);
    };
  }, [axiosInstance]);

  return (
    <RequestLoggerContext.Provider value={{ logs, clearLogs }}>
      {children}
    </RequestLoggerContext.Provider>
  );
};

export const useRequestLogger = () => {
  const context = useContext(RequestLoggerContext);
  if (context === undefined) {
    throw new Error('useRequestLogger must be used within a RequestLoggerProvider');
  }
  return context;
};

export const RequestLoggerDisplay: React.FC = () => {
  const { logs, clearLogs } = useRequestLogger();
  const [isOpen, setIsOpen] = useState<boolean>(() => {
    const savedState = localStorage.getItem('requestLoggerOpen');
    return savedState ? JSON.parse(savedState) : false;
  });
  const logDisplayRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    localStorage.setItem('requestLoggerOpen', JSON.stringify(isOpen));
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && logDisplayRef.current) {
      logDisplayRef.current.scrollTop = 0;
    }
  }, [logs.length, isOpen]);

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation(); // Зупиняємо спливання, щоб не закрити панель
    clearLogs();
  };

  return (
    <div 
      style={{
        position: 'fixed',
        bottom: '10px',
        left: '50%', // Центрування по горизонталі
        transform: 'translateX(-50%)', // Зсув на половину ширини елемента
        zIndex: 9999,
        backgroundColor: 'rgba(0,0,0,0.7)',
        color: 'white',
        padding: '10px',
        borderRadius: '5px',
        width: isOpen ? '400px' : 'auto',
        fontFamily: 'monospace',
        fontSize: '12px',
      }}
      onClick={() => setIsOpen(!isOpen)}
    >
      <h3 style={{ margin: 0, fontSize: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}>
        <span>
          Backend Requests ({logs.length}) {isOpen ? '▲' : '▼'}
        </span>
        {isOpen && (
          <button onClick={handleClear} style={{ background: '#555', color: 'white', border: 'none', borderRadius: '3px', cursor: 'pointer', padding: '2px 5px', fontSize: '10px', marginLeft: '10px' }}>
            Clear
          </button>
        )}
      </h3>
      {isOpen && (
        <div ref={logDisplayRef} style={{ marginTop: '10px', maxHeight: '250px', overflowY: 'auto' }}>
          {logs.map((log) => (
            <div key={log.id} style={{ borderBottom: '1px solid #333', paddingBottom: '5px', marginBottom: '5px' }}>
              <p style={{ margin: 0, fontSize: '12px' }}>
                <strong style={{ color: log.status && log.status >= 200 && log.status < 300 ? 'lightgreen' : 'red' }}>
                  [{log.status || '...'}]
                </strong> {log.method} {log.url}
              </p>
              {log.error && <p style={{ margin: 0, fontSize: '10px', color: 'red' }}>Error: {log.error}</p>}
              {log.requestBody && <p style={{ margin: 0, fontSize: '10px' }}>Req: {JSON.stringify(log.requestBody).substring(0, 50)}...</p>}
              {log.responseBody && <p style={{ margin: 0, fontSize: '10px' }}>Res: {JSON.stringify(log.responseBody).substring(0, 50)}...</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
